/* global Word, Office */

import * as React from "react";

export type DocumentSelectionContext = {
  text: string;
  previewText: string;
};

const DEBOUNCE_MS = 300;
const MIN_SELECTION_LEN = 1;

function debounce(fn: () => void, ms: number): () => void {
  let t: ReturnType<typeof setTimeout> | null = null;
  return () => {
    if (t) {
      clearTimeout(t);
    }
    t = setTimeout(() => {
      t = null;
      fn();
    }, ms);
  };
}

export type UseDocumentSelectionOptions = {
  enabled: boolean;
};

/** Tracks the current Word selection for the Collaborate tab and selection action bar. */
export function useDocumentSelection({ enabled }: UseDocumentSelectionOptions) {
  const trackedRangeRef = React.useRef<Word.Range | null>(null);
  const trackingContextRef = React.useRef<Word.RequestContext | null>(null);
  const [selectedContext, setSelectedContext] = React.useState<DocumentSelectionContext | null>(null);
  const selectedTextRef = React.useRef("");

  const releaseTrackedRangeOnly = React.useCallback(async () => {
    if (trackingContextRef.current && trackedRangeRef.current) {
      try {
        trackingContextRef.current.trackedObjects.remove(trackedRangeRef.current);
        await trackingContextRef.current.sync();
      } catch {
        /* ignore cleanup errors */
      }
    }
    trackedRangeRef.current = null;
    trackingContextRef.current = null;
  }, []);

  const clearSelection = React.useCallback(async () => {
    await releaseTrackedRangeOnly();
    selectedTextRef.current = "";
    setSelectedContext(null);
  }, [releaseTrackedRangeOnly]);

  const handleSelectionChange = React.useCallback(async () => {
    if (!enabled) {
      return;
    }

    await releaseTrackedRangeOnly();

    try {
      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load("text");
        await context.sync();

        const text = (selection.text ?? "").trim();
        if (text.length < MIN_SELECTION_LEN) {
          selectedTextRef.current = "";
          setSelectedContext(null);
          return;
        }

        context.trackedObjects.add(selection);
        await context.sync();

        trackedRangeRef.current = selection;
        trackingContextRef.current = context;
        selectedTextRef.current = text;

        setSelectedContext({
          text,
          previewText: text.length > 80 ? `${text.slice(0, 80)}…` : text,
        });
      });
    } catch {
      selectedTextRef.current = "";
      setSelectedContext(null);
    }
  }, [enabled, releaseTrackedRangeOnly]);

  React.useEffect(() => {
    if (!enabled) {
      void clearSelection();
      return undefined;
    }

    const debounced = debounce(() => {
      void handleSelectionChange();
    }, DEBOUNCE_MS);

    const add = () => {
      try {
        Office.context.document.addHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          debounced,
          () => {}
        );
      } catch {
        /* no-op */
      }
    };

    if (typeof Office !== "undefined" && Office.context?.document) {
      add();
    }

    void handleSelectionChange();

    return () => {
      try {
        Office.context.document.removeHandlerAsync(
          Office.EventType.DocumentSelectionChanged,
          { handler: debounced },
          () => {}
        );
      } catch {
        /* no-op */
      }
      void clearSelection();
    };
  }, [enabled, handleSelectionChange, clearSelection]);

  return {
    selectedContext,
    clearSelection,
    getSelectionQuote: () => selectedTextRef.current,
  };
}
